import { useTran } from "@/hooks";
import { observer } from "mobx-react-lite";
import ESGReportsHeader from "../components/reports/reportList/ESGReportsHeader";
import { ResizableSidebar } from "@/components/common";
import {
  OrganizationTree,
  useOrganizationStore,
} from "@/features/organization";
import { useEffect } from "react";
import { useAuthStore } from "@/features/auth";
import { useESGReportListStore } from "../hooks/useESGReportListStore";
import ESGReportsFilter from "../components/reports/reportList/ESGReportsFilter";
import ESGReportCard from "../components/reports/reportList/ESGReportCard";
import { useDebounce } from "use-debounce";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import ESGReportsEmptyState from "../components/reports/reportList/ESGReportsEmptyState";

const ESGReportListPage = observer(() => {
  const { t } = useTran();

  const { user } = useAuthStore();
  const {
    fetchHierarchy,
    tree,
    loading: orgLoading,
    selectedId: selectedOrgId,
  } = useOrganizationStore();

  const {
    page,
    limit,
    search,
    selectedFramework,
    selectedStatus,
    fetchReportList,
    reports,
    loading,
    deleting,

    openDeleteDialog,
    setOpenDeleteDialog,

    selectedReportToDelete,
    deleteReport,
  } = useESGReportListStore();

  const [debouncedSearch] = useDebounce(search, 400);

  useEffect(() => {
    if (orgLoading || tree.length) return;
    fetchHierarchy({ selectId: user.organizationUnitId });
  }, [fetchHierarchy, user.organizationUnitId, tree.length]);

  useEffect(() => {
    fetchReportList(selectedOrgId);
  }, [
    page,
    limit,
    debouncedSearch,
    selectedFramework,
    selectedStatus,
    selectedOrgId,
    fetchReportList,
  ]);

  return (
    <div className="space-y-6">
      <ESGReportsHeader />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <ResizableSidebar
          storageKey="nxstart_esg_report_tree_width"
          handleTitle={t("Drag to resize")}
        >
          <OrganizationTree />
        </ResizableSidebar>

        <div className="flex-1 space-y-4">
          <ESGReportsFilter />
          {!loading && reports.length === 0 ? (
            <ESGReportsEmptyState />
          ) : (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 2xl:grid-cols-3">
              {reports.map((report, index) => {
                return (
                  <ESGReportCard
                    report={report}
                    key={index}
                    openDeleteDialog={openDeleteDialog}
                    setOpenDeleteDialog={setOpenDeleteDialog}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={openDeleteDialog}
        onOpenChange={(openState) => {
          if (!openState) {
            setOpenDeleteDialog(false);
          }
        }}
        onConfirm={() => deleteReport(selectedReportToDelete?._id)}
        title={t("Delete Report")}
        description={t(
          `Are you sure you want to delete "${selectedReportToDelete?.reportName}"? This action cannot be undone.`,
        )}
        confirmLabel={t("Delete")}
        cancelLabel={t("Cancel")}
        tone="danger"
        loading={Boolean(deleting)}
      />
    </div>
  );
});

export default ESGReportListPage;
